import { applyTenantRequestHeaders } from './tenantRequestContext';

const OUTREACH_ATTRIBUTION_KEY = 'tradeproof:outreach-attribution';
const PENDING_LOGIN_KEY = 'tradeproof:pending-demo-login';
const MAX_LOGIN_RECORD_ATTEMPTS = 3;
let attemptedVisitToken: string | null = null;

function readSessionValue(key: string): string | null {
  try { return sessionStorage.getItem(key); } catch { return null; }
}

function writeSessionValue(key: string, value: string): void {
  try { sessionStorage.setItem(key, value); } catch {}
}

function readDurableValue(key: string): string | null {
  try { return localStorage.getItem(key); } catch { return null; }
}

function writeDurableValue(key: string, value: string): void {
  try { localStorage.setItem(key, value); } catch {}
}

function removeDurableValue(key: string): void {
  try { localStorage.removeItem(key); } catch {}
}

function getAnonymousSessionId(): string {
  const existing = readSessionValue('tradeproof:outreach-session-id');
  if (existing) return existing;
  let created = '';
  try {
    created = typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : '';
  } catch {}
  created ||= `session-${Math.random().toString(36).slice(2)}-${Date.now().toString(36)}`;
  writeSessionValue('tradeproof:outreach-session-id', created);
  return created;
}

function getOutreachToken(): string | null {
  try {
    const token = new URLSearchParams(window.location.search || '').get('outreach');
    return String(token || '').trim() || null;
  } catch { return null; }
}

export async function recordOutreachLinkVisit(): Promise<void> {
  const token = getOutreachToken();
  if (!token || attemptedVisitToken === token) return;
  attemptedVisitToken = token;
  // Keep the exact token available even if navigation removes the query string
  // before the visit request completes. The server remains authoritative.
  writeDurableValue(OUTREACH_ATTRIBUTION_KEY, token);
  try {
    const response = await fetch('/api/outreach/visit', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token, anonymousSessionId: getAnonymousSessionId() }),
    });
    const payload = await response.json().catch(() => ({})) as { recorded?: boolean };
    if (response.ok && payload.recorded === true) writeSessionValue(OUTREACH_ATTRIBUTION_KEY, token);
  } catch {}
}

function readAttributionToken(): string | null {
  return readSessionValue(OUTREACH_ATTRIBUTION_KEY) || readDurableValue(OUTREACH_ATTRIBUTION_KEY);
}

function readPendingLogin(): { token: string | null; anonymousSessionId: string } | null {
  try {
    const parsed = JSON.parse(readDurableValue(PENDING_LOGIN_KEY) || 'null');
    if (!parsed || typeof parsed !== 'object' || !parsed.anonymousSessionId) return null;
    return { token: typeof parsed.token === 'string' ? parsed.token : null, anonymousSessionId: String(parsed.anonymousSessionId) };
  } catch { return null; }
}

function writePendingLogin(value: { token: string | null; anonymousSessionId: string }): void {
  writeDurableValue(PENDING_LOGIN_KEY, JSON.stringify(value));
}

async function sendLoginSuccess(accessToken: string, pending: { token: string | null; anonymousSessionId: string }): Promise<boolean> {
  const headers = applyTenantRequestHeaders({
    Authorization: `Bearer ${accessToken}`,
    'Content-Type': 'application/json',
  });
  for (let attempt = 0; attempt < MAX_LOGIN_RECORD_ATTEMPTS; attempt += 1) {
    try {
      const response = await fetch('/api/outreach/login-success', {
        method: 'POST',
        headers,
        body: JSON.stringify({
          anonymousSessionId: pending.anonymousSessionId,
          attributionToken: pending.token,
        }),
      });
      if (response.ok) return true;
    } catch {}
    if (attempt < MAX_LOGIN_RECORD_ATTEMPTS - 1) {
      await new Promise((resolve) => window.setTimeout(resolve, 250 * (attempt + 1)));
    }
  }
  return false;
}

export async function recordDemoLoginSuccess(session: { access_token?: string } | null | undefined): Promise<void> {
  const accessToken = String(session?.access_token || '').trim();
  if (!accessToken) return;
  // A new authenticated session gets a fresh logical event. Never reuse a
  // pending token from an earlier account or tab; the backend also validates
  // the token against the authenticated demo identity.
  const pending = { token: readAttributionToken(), anonymousSessionId: getAnonymousSessionId() };
  writePendingLogin(pending);
  if (await sendLoginSuccess(accessToken, pending)) {
    removeDurableValue(PENDING_LOGIN_KEY);
    removeDurableValue(OUTREACH_ATTRIBUTION_KEY);
    try { sessionStorage.removeItem(OUTREACH_ATTRIBUTION_KEY); } catch {}
  }
}

export async function retryPendingDemoLoginSuccess(session: { access_token?: string } | null | undefined): Promise<void> {
  const accessToken = String(session?.access_token || '').trim();
  const pending = readPendingLogin();
  if (!accessToken || !pending) return;
  if (await sendLoginSuccess(accessToken, pending)) {
    removeDurableValue(PENDING_LOGIN_KEY);
    removeDurableValue(OUTREACH_ATTRIBUTION_KEY);
    try { sessionStorage.removeItem(OUTREACH_ATTRIBUTION_KEY); } catch {}
  }
}
